import { ActionType, BootstrapAction } from '@/actions/actiontypes';
import { handleError } from '@/actions/metaActions';
import { fetchAllOmradeGeometries } from '@/middleware/fetchMisc';
import { Integration } from '@/middleware/integration';
import { bindErrorCatcher, hashChangeEventListeners } from '@/middleware/middleware';
import { RootState, Severity, VegkartError } from '@/state';
import { useDispatch } from '@/state/store';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { bootstrap } from './bootstrap';
import { useDataLoader } from './useDataLoader';

interface BootstrapperProps {
    children: ReactNode;
    fallback: ReactNode;
    integration: Integration;
    store: RootState;
}

export const Bootstrapper = ({
    children,
    fallback,
    integration: integration,
    store,
}: BootstrapperProps) => {
    const dispatch = useDispatch();
    const { data, datakatalogServiceUnavailable } = useDataLoader();
    const [ready, setReady] = useState(store.bootstrapped);
    const warningShown = useRef(false);
    const bootstrapStarted = useRef(store.bootstrapped);
    const bootstrapInFlight = useRef(false);

    useEffect(() => {
        const eventListeners = hashChangeEventListeners(dispatch);
        eventListeners.forEach(listener =>
            window.addEventListener(listener.type, listener.handler, false)
        );
        return () => {
            eventListeners.forEach(listener =>
                window.removeEventListener(listener.type, listener.handler, false)
            );
        };
    }, [dispatch]);

    useEffect(() => {
        if (!data || bootstrapStarted.current || bootstrapInFlight.current) return;

        bootstrapInFlight.current = true;
        const timeBeforeFetch = new Date().getTime();

        bootstrap(integration, store, data)
            .then(bootstrappedState => {
                bootstrapStarted.current = true;

                const timeAfterFetch = new Date().getTime();
                const bootstrappedAction: BootstrapAction = {
                    type: ActionType.BOOTSTRAP,
                    bootstrappedState: bootstrappedState.withConfig(integration.config),
                };
                integration.server.postPerformanceLogEntry(
                    ActionType.BOOTSTRAP,
                    timeAfterFetch - timeBeforeFetch
                );
                dispatch(bootstrappedAction);
                fetchAllOmradeGeometries()(dispatch, bootstrappedState);
                bindErrorCatcher(dispatch, integration);

                if (datakatalogServiceUnavailable && !warningShown.current) {
                    warningShown.current = true;
                    handleError(
                        new VegkartError(
                            'Kunne ikke laste inn data fra Datakatalogen. Bruker data fra forrige økt.',
                            undefined,
                            Severity.WARN
                        )
                    )(dispatch, () => bootstrappedState, integration);
                }
            })
            .finally(() => {
                bootstrapInFlight.current = false;
                setReady(true);
            });
    }, [data, datakatalogServiceUnavailable, dispatch, integration, store]);

    return useMemo(
        () => (ready ? <>{children}</> : <>{fallback}</>),
        [children, fallback, ready]
    );
};
